import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { registerUser, signInUser, resendVerificationEmail } from '../services/userService';
import { 
  Mail, 
  Lock, 
  User, 
  Eye, 
  EyeOff, 
  Sparkles, 
  Shield,
  CheckCircle,
  Send,
  AlertCircle,
  Loader2,
  XCircle
} from 'lucide-react';

const AuthForm = ({ mode = 'login', onToggleMode, onSuccess }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    displayName: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [formErrors, setFormErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (mode === 'signup' && formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    if (mode === 'signup' && !formData.displayName) {
      errors.displayName = 'Display name is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: '❌ Validation Error',
        description: 'Please fix the errors in the form.',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    setFormErrors({});

    try {
      let result;
      
      if (mode === 'signup') {
        result = await registerUser(formData.email, formData.password, formData.displayName);
        
        if (result.success) {
          // SHOW VERIFICATION SCREEN
          setPendingEmail(formData.email);
          setVerificationSent(true);
          toast({
            title: '🎉 Account Created!',
            description: 'Please check your email for the verification link.',
          });
        } else {
          if (result.error.includes('email') || result.error.includes('account')) {
            setFormErrors(prev => ({ ...prev, email: result.error }));
          } else {
            toast({
              title: '❌ Sign Up Failed',
              description: result.error,
              variant: 'destructive'
            });
          }
        }
      } else {
        result = await signInUser(formData.email, formData.password);
        if (result.success) {
          toast({
            title: '✅ Welcome Back!',
            description: result.message,
          });
          onSuccess?.();
        } else {
          if (result.needsVerification) {
            setPendingEmail(formData.email);
            setVerificationSent(true);
            toast({
              title: '📧 Verification Required',
              description: result.error,
            });
          } else {
            if (result.error.includes('email') || result.error.includes('account')) {
              setFormErrors(prev => ({ ...prev, email: result.error }));
            } else if (result.error.includes('password')) {
              setFormErrors(prev => ({ ...prev, password: result.error }));
            } else {
              toast({
                title: '❌ Sign In Failed',
                description: result.error,
                variant: 'destructive'
              });
            }
          }
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
      toast({
        title: '❌ Unexpected Error',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    setLoading(true);
    const result = await resendVerificationEmail();
    setLoading(false);
    
    if (result.success) {
      toast({
        title: '📧 Email Sent!',
        description: 'Check your inbox for the verification link.',
      });
    } else {
      toast({
        title: '❌ Failed to Send',
        description: result.error,
        variant: 'destructive'
      });
    }
  };

  const handleBackToAuth = () => {
    setVerificationSent(false);
    setPendingEmail('');
  };

  // VERIFICATION SCREEN - SHOWS IMMEDIATELY AFTER SUCCESSFUL SIGNUP
  if (verificationSent) {
    return (
      <Card className="auth-card glass" style={{ borderRadius: '0px', padding: '40px', maxWidth: '480px', margin: '0 auto' }}>
        <div className="text-center space-y-6">
          <div className="w-20 h-20 mx-auto bg-gradient-to-r from-[#00FFD1] to-[#0EA5FF] rounded-full flex items-center justify-center">
            <Send className="w-10 h-10 text-black" />
          </div>
          
          <div>
            <h2 className="text-3xl font-bold mb-2" style={{ color: '#FFFFFF', fontFamily: 'Orbitron, sans-serif' }}>
              Check Your Email
            </h2>
            <p className="text-lg mb-2" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
              We sent a verification link to:
            </p>
            <p className="text-xl font-semibold" style={{ color: '#00FFD1' }}>
              {pendingEmail}
            </p>
          </div>

          <div className="space-y-4">
            <div className="flex items-start gap-3 justify-center p-4 glass text-left" style={{ borderRadius: '0px' }}>
              <CheckCircle className="w-6 h-6 mt-0.5 flex-shrink-0" style={{ color: '#00FFD1' }} />
              <div>
                <p style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                  <strong>Click the verification link</strong> in the email to activate your account
                </p>
                <p className="text-sm mt-2" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  The email will be from <strong>noreply@firebaseapp.com</strong>
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 justify-center p-4 glass text-left" style={{ borderRadius: '0px' }}>
              <AlertCircle className="w-6 h-6 mt-0.5 flex-shrink-0" style={{ color: '#FFA500' }} />
              <div>
                <p style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                  <strong>Can't find the email?</strong> Check your spam folder
                </p>
                <p className="text-sm mt-1" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Unverified accounts are automatically removed after 24 hours
                </p>
              </div>
            </div>
            
            <Button
              onClick={resendVerification}
              disabled={loading}
              className="w-full"
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#FFFFFF',
                borderRadius: '0px',
                padding: '16px',
                border: '1px solid rgba(0, 255, 209, 0.3)',
              }}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Resend Verification Email
                </>
              )}
            </Button>

            <Button
              onClick={handleBackToAuth}
              variant="ghost"
              className="w-full"
              style={{
                color: '#00FFD1',
                borderRadius: '0px',
              }}
            >
              Back to {mode === 'signup' ? 'Sign Up' : 'Sign In'}
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="auth-card glass" style={{ borderRadius: '0px', padding: '40px', maxWidth: '480px', margin: '0 auto' }}>
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-3 mb-4">
          <Sparkles className="w-8 h-8" style={{ color: '#00FFD1' }} />
          <h2 className="text-4xl font-bold" style={{ color: '#FFFFFF', fontFamily: 'Orbitron, sans-serif' }}>
            {mode === 'login' ? 'Welcome Back' : 'Join Elite Hub'}
          </h2>
        </div>
        <p className="text-lg" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
          {mode === 'login' 
            ? 'Sign in to access premium plugins' 
            : 'Create your account to get started'
          }
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {mode === 'signup' && (
          <div className="space-y-2">
            <Label htmlFor="displayName" className="text-white">
              Display Name *
            </Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: '#00FFD1' }} />
              <Input
                id="displayName"
                name="displayName"
                type="text"
                value={formData.displayName}
                onChange={handleChange}
                required
                placeholder="Enter your name"
                className={`glass pl-12 ${formErrors.displayName ? 'border-red-500' : ''}`}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: formErrors.displayName ? '1px solid #EF4444' : '1px solid rgba(255, 255, 255, 0.25)',
                  color: '#FFFFFF',
                  borderRadius: '0px',
                  padding: '14px 14px 14px 48px',
                  fontSize: '16px',
                }}
              />
            </div>
            {formErrors.displayName && (
              <div className="flex items-center gap-2 text-red-400 text-sm">
                <XCircle className="w-4 h-4" />
                {formErrors.displayName}
              </div>
            )}
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="email" className="text-white">
            Email Address *
          </Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: '#00FFD1' }} />
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Enter your email"
              className={`glass pl-12 ${formErrors.email ? 'border-red-500' : ''}`}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: formErrors.email ? '1px solid #EF4444' : '1px solid rgba(255, 255, 255, 0.25)',
                color: '#FFFFFF',
                borderRadius: '0px',
                padding: '14px 14px 14px 48px',
                fontSize: '16px',
              }}
            />
          </div>
          {formErrors.email && (
            <div className="flex items-center gap-2 text-red-400 text-sm">
              <XCircle className="w-4 h-4" />
              {formErrors.email}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="password" className="text-white">
            Password *
          </Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: '#00FFD1' }} />
            <Input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              required
              placeholder="Enter your password"
              className={`glass pr-12 pl-12 ${formErrors.password ? 'border-red-500' : ''}`}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: formErrors.password ? '1px solid #EF4444' : '1px solid rgba(255, 255, 255, 0.25)',
                color: '#FFFFFF',
                borderRadius: '0px',
                padding: '14px 48px 14px 48px',
                fontSize: '16px',
              }}
              minLength={6}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2"
              style={{ color: '#00FFD1' }}
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {formErrors.password && (
            <div className="flex items-center gap-2 text-red-400 text-sm">
              <XCircle className="w-4 h-4" />
              {formErrors.password}
            </div>
          )}
          {mode === 'signup' && !formErrors.password && (
            <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Password must be at least 6 characters
            </p>
          )}
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="w-full relative overflow-hidden group"
          style={{
            background: '#00FFD1',
            color: '#000000',
            borderRadius: '0px',
            padding: '16px',
            fontSize: '18px',
            fontWeight: 600,
            border: 'none',
            transition: 'all 0.4s ease-in-out',
          }}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              {mode === 'login' ? 'Signing In...' : 'Creating Account...'}
            </div>
          ) : (
            <>
              <Shield className="w-5 h-5 mr-2 inline" />
              {mode === 'login' ? 'Sign In' : 'Create Account'}
            </>
          )}
        </Button>

        <div className="text-center">
          <button
            type="button"
            onClick={onToggleMode}
            className="text-lg transition-colors duration-300 hover:text-[#00FFD1]"
            style={{ color: 'rgba(255, 255, 255, 0.85)' }}
          >
            {mode === 'login' 
              ? "Don't have an account? Sign Up" 
              : 'Already have an account? Sign In'
            }
          </button>
        </div>

        <div className="flex items-center gap-3 p-4 glass" style={{ borderRadius: '0px' }}>
          <Shield className="w-5 h-5 flex-shrink-0" style={{ color: '#00FFD1' }} />
          <span className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
            {mode === 'signup' 
              ? 'We\'ll send a verification email to activate your account. Unverified accounts are automatically removed after 24 hours.' 
              : 'Secure authentication with email verification required.'
            }
          </span>
        </div>
      </form>
    </Card>
  );
};

export default AuthForm;